import { AnalyticsViewExerciseDetailOrigin } from "@domain/library/exercise-analytics";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { Session } from "@domain/program/session";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { DayColumnHeader } from "@ui/pages/program-builder/day-table/day-column-header";
import { DayColumnMenuButton } from "@ui/pages/program-builder/day-table/day-column-menu-button";
import {
	DayExercisePlaceholder,
	ITEM_SPACING,
	SESSION_DROPPABLE_HORIZONTAL_PADDING,
} from "@ui/pages/program-builder/day-table/day-exercise-placeholder";
import { NoExercisePlaceholder } from "@ui/pages/program-builder/day-table/no-exercise-placeholder";
import { ExerciseDetailsDialogTabs } from "@ui/pages/program-builder/dialog/exercise-details-dialog";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { useExerciseDragAndDrop } from "@ui/pages/program-builder/hooks/use-exercise-drag-and-drop";
import { DraggableExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { colors } from "@ui/style/colors";
import { stack } from "@ui/style/layout";
import { getScrollbarStyle } from "@ui/style/scrollbar";

import { AnimatePresence } from "framer-motion";
import { useObservable } from "micro-observables";
import { duplicateSessionHaloVisible } from "@ui/pages/program-builder/quickstart/duplicate-session-signal";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Droppable } from "@hello-pangea/dnd";

interface DayColumnProps {
	title: string;
	session: Session;
	index: number;
	isDropDisabled?: boolean;
	updatedExerciseId?: string;
	programEditor: ProgramContentEditor;
}

export const DAY_COLUMN_WIDTH = 300;
const CARD_HEIGHT = 170;

export const DayColumn: React.FC<DayColumnProps> = ({
	title,
	session,
	index,
	isDropDisabled,
	updatedExerciseId,
	programEditor,
}) => {
	const editedProgram = useObservable(programEditor.editedProgram);
	const { openExerciseDetailsModal } = useEditorExerciseCardActions();
	const { shouldAnimate } = useExerciseDragAndDrop();

	const [isHovered, setIsHovered] = useState(false);
	const duplicateHalo = useObservable(duplicateSessionHaloVisible);

	const firstNonEmptyDayIndex = React.useMemo(() => {
		const entries = editedProgram.days.entries;
		for (let i = 0; i < entries.length; i++) {
			const [dayIdx, sess] = entries[i];
			if (sess.exercises.length > 0) return dayIdx;
		}
		return -1;
	}, [editedProgram]);

	const componentRef = useRef<HTMLDivElement | null>(null);

	const [componentHeight, setComponentHeight] = useState(0);
	useLayoutEffect(() => {
		if (componentRef.current) {
			setComponentHeight(componentRef.current.clientHeight);
		}
	}, [componentRef.current?.clientHeight]);

	const allowDayColumnScroll = useMemo(
		() => componentHeight < CARD_HEIGHT * (session.exercises.length + 1),
		[componentHeight, session.exercises.length],
	);

	return (
		<DayColumnContent
			ref={componentRef}
			onMouseEnter={() => {
				setIsHovered(true);
			}}
			onMouseOver={() => {
				setIsHovered(true);
			}}
			onMouseLeave={() => {
				setIsHovered(false);
			}}
		>
			<ColumnTitle className="p-size-l-bold">
				{title}
				{session.exercises.length > 0 && (
					<DayColumnMenuButton
						editedProgram={editedProgram}
						dayIndex={index}
						showQuickstartHalo={Boolean(duplicateHalo && index === firstNonEmptyDayIndex)}
					/>
				)}
			</ColumnTitle>
			<ColumnContent isHovered={isHovered}>
				<StyledDayColumnHeader session={session} />
				<Droppable droppableId={index.toString()} isDropDisabled={isDropDisabled}>
					{(provided, snapshot) => (
						<List
							id={"droppable_" + index}
							ref={provided.innerRef}
							isDraggingOver={snapshot.isDraggingOver}
							allowScrolling={allowDayColumnScroll}
							{...provided.droppableProps}
						>
							<ConditionalAnimateSharedLayout active={shouldAnimate}>
								{session.exercises.length === 0 && <NoExercisePlaceholder />}
								{session.exercises.map((exercise, exerciseIndex) => (
									<DraggableExerciseCard
										className="not-provided-placeholder"
										key={exercise.draggableId}
										exercise={exercise}
										programEditor={programEditor}
										index={exerciseIndex}
										shouldAnimate={shouldAnimate}
										onClick={() =>
											openExerciseDetailsModal(exercise, session.exercises, AnalyticsViewExerciseDetailOrigin.CANVAS)
										}
										onCommentIconClick={() =>
											openExerciseDetailsModal(
												exercise,
												session.exercises,
												AnalyticsViewExerciseDetailOrigin.CANVAS,
												ExerciseDetailsDialogTabs.COMMENT,
											)
										}
										updatedExerciseId={updatedExerciseId}
									/>
								))}
							</ConditionalAnimateSharedLayout>
							{provided.placeholder}
							<DayExercisePlaceholder
								className="not-provided-placeholder"
								dayIndex={index}
								display={snapshot.isDraggingOver}
							/>
						</List>
					)}
				</Droppable>
			</ColumnContent>
		</DayColumnContent>
	);
};

export const ConditionalAnimateSharedLayout: React.FC<{
	active: boolean;
	children: React.ReactNode;
}> = ({ active, children }) =>
	active ? <AnimatePresence presenceAffectsLayout>{children}</AnimatePresence> : <>{children}</>;

const spacedStack = () => stack(0, "flexstart", "stretch");

const DayColumnContent = styled.div`
	${spacedStack()};
	flex: 1 !important;
	width: ${DAY_COLUMN_WIDTH}px;
	min-height: 0;
	background-color: ${colors.white};
	border: 1px solid transparent;
`;

const ColumnTitle = styled.div`
	text-align: center;
	display: grid;
	position: relative;
	place-items: center;
	height: 44px;
`;

const ColumnContent = styled.div<{ isHovered: boolean }>`
	border-radius: 16px;
	${spacedStack()};
	background-color: ${colors.primary10};
	flex: 1;
	min-height: 0;

	${({ isHovered }) =>
		isHovered &&
		css`
			outline: 1px solid ${colors.primary25};
		`}
`;

const StyledDayColumnHeader = styled(DayColumnHeader)`
	margin: ${SESSION_DROPPABLE_HORIZONTAL_PADDING}px;
`;

const List = styled.div<{ isDraggingOver: boolean; allowScrolling: boolean }>`
	${stack(undefined, "flexstart", "stretch")};
	padding: 0 ${SESSION_DROPPABLE_HORIZONTAL_PADDING}px;
	flex: 1 1 auto;
	overflow-y: ${({ allowScrolling }) => (allowScrolling ? "auto" : "unset")};
	max-width: 100%;
	height: 150px;
	position: relative;

	> * {
		margin-bottom: ${ITEM_SPACING}px;

		&:not(.not-provided-placeholder) {
			width: 100% !important;
		}
	}

	${getScrollbarStyle(6)};
	// css for browser supporting -webkit-scrollbar which is not the case of Firefox
	@supports not (-moz-appearance: none) {
		padding-right: ${SESSION_DROPPABLE_HORIZONTAL_PADDING - 2}px;
		margin-right: 2px;
	}
`;

